import { useState, useEffect, useCallback } from 'react';
import { SEO } from '../seo/SEO';
import { useI18n } from '../i18n';
import { useRoutes } from '../hooks/useRoutes';
import { AppHeader } from '../components/AppHeader';
import { TutorialContent } from '../components/TutorialContent';
import './TutorialPage.css';

type Cell = number | null;
type Board = Cell[][];
type Phase = 'idle' | 'waiting' | 'success' | 'wrong';

interface Scenario {
  type: 'info' | 'action';
  board: Board;
  targetCell?: [number, number];
  targetValue?: number;
  highlightRow?: number;
  highlightCol?: number;
  highlightBox?: [number, number]; // [topRow, leftCol]
}

function emptyBoard(): Board {
  return Array.from({ length: 9 }, () => Array(9).fill(null));
}

// Solution used: classic Sudoku
// 5 3 4 6 7 8 9 1 2
// 6 7 2 1 9 5 3 4 8
// 1 9 8 3 4 2 5 6 7
// 8 5 9 7 6 1 4 2 3
// 4 2 6 8 5 3 7 9 1
// 7 1 3 9 2 4 8 5 6
// 9 6 1 5 3 7 2 8 4
// 2 8 7 4 1 9 6 3 5
// 3 4 5 2 8 6 1 7 9

const SCENARIOS: Scenario[] = [
  // Step 0: Grid intro — empty board
  {
    type: 'info',
    board: emptyBoard(),
  },
  // Step 1: The rule — empty board
  {
    type: 'info',
    board: emptyBoard(),
  },
  // Step 2: Row Naked Single — row 3: [8,5,9,7,6,1,4,2,?] → (3,8)=3
  {
    type: 'action',
    board: (() => {
      const b = emptyBoard();
      b[3] = [8, 5, 9, 7, 6, 1, 4, 2, null];
      return b;
    })(),
    targetCell: [3, 8],
    targetValue: 3,
    highlightRow: 3,
  },
  // Step 3: Column Naked Single — col 5: [8,5,2,1,3,4,7,?,6] → (7,5)=9
  {
    type: 'action',
    board: (() => {
      const b = emptyBoard();
      const colVals: Cell[] = [8, 5, 2, 1, 3, 4, 7, null, 6];
      colVals.forEach((v, r) => { b[r][5] = v; });
      return b;
    })(),
    targetCell: [7, 5],
    targetValue: 9,
    highlightCol: 5,
  },
  // Step 4: Box Naked Single — box(0,0): 8 given → (2,2)=8
  {
    type: 'action',
    board: (() => {
      const b = emptyBoard();
      b[0][0] = 5; b[0][1] = 3; b[0][2] = 4;
      b[1][0] = 6; b[1][1] = 7; b[1][2] = 2;
      b[2][0] = 1; b[2][1] = 9; // b[2][2] = null → target = 8
      return b;
    })(),
    targetCell: [2, 2],
    targetValue: 8,
    highlightBox: [0, 0],
  },
  // Step 5: Completion — empty board
  {
    type: 'info',
    board: emptyBoard(),
  },
];

export function TutorialPage() {
  const { nav } = useRoutes();
  const { t } = useI18n();

  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState<Phase>('idle');

  const scenario = SCENARIOS[step];
  const isLastStep = step === SCENARIOS.length - 1;

  // Reset phase when step changes
  useEffect(() => {
    setPhase(SCENARIOS[step].type === 'action' ? 'waiting' : 'idle');
  }, [step]);

  // Auto-advance after correct answer
  useEffect(() => {
    if (phase !== 'success') return;
    const timer = setTimeout(() => {
      if (step < SCENARIOS.length - 1) {
        setStep(s => s + 1);
      }
    }, 1300);
    return () => clearTimeout(timer);
  }, [phase, step]);

  const handleInput = useCallback((n: number) => {
    if (SCENARIOS[step].type !== 'action' || phase !== 'waiting') return;
    if (n === SCENARIOS[step].targetValue) {
      setPhase('success');
    } else {
      setPhase('wrong');
      setTimeout(() => setPhase('waiting'), 1500);
    }
  }, [step, phase]);

  // Keyboard support
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const n = parseInt(e.key);
      if (n >= 1 && n <= 9) handleInput(n);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleInput]);

  const goNext = () => {
    if (isLastStep) {
      try { localStorage.setItem('tutorial_completed_v1', '1'); } catch {}
      try { localStorage.setItem('onboarding_prompt_seen_v1', '1'); } catch {}
      nav('play');
    } else {
      setStep(s => s + 1);
    }
  };

  const goPrev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const getCellClass = (r: number, c: number): string => {
    const sc = SCENARIOS[step];
    const val = sc.board[r][c];
    const isTarget = !!sc.targetCell && r === sc.targetCell[0] && c === sc.targetCell[1];

    const classes = ['tut-cell'];
    // Thick 3x3 box borders
    if (c === 2 || c === 5) classes.push('tut-cell--rb');
    if (r === 2 || r === 5) classes.push('tut-cell--bb');

    if (sc.type === 'info') {
      classes.push('tut-cell--dim');
      return classes.join(' ');
    }

    let inConstraint = false;
    if (sc.highlightRow !== undefined && r === sc.highlightRow) inConstraint = true;
    if (sc.highlightCol !== undefined && c === sc.highlightCol) inConstraint = true;
    if (sc.highlightBox) {
      const [br, bc] = sc.highlightBox;
      if (r >= br && r < br + 3 && c >= bc && c < bc + 3) inConstraint = true;
    }

    if (isTarget) {
      classes.push('tut-cell--target');
      if (phase === 'success') classes.push('tut-cell--success');
      else if (phase === 'wrong') classes.push('tut-cell--wrong');
    } else if (inConstraint && val !== null) {
      classes.push('tut-cell--given');
    } else {
      classes.push('tut-cell--dim');
    }

    return classes.join(' ');
  };

  const getDisplayValue = (r: number, c: number): number | null => {
    const sc = SCENARIOS[step];
    if (sc.targetCell && r === sc.targetCell[0] && c === sc.targetCell[1]) {
      return phase === 'success' ? sc.targetValue! : null;
    }
    return sc.board[r][c];
  };

  const stepData = [
    { title: t.tutorial.step0Title, desc: t.tutorial.step0Desc },
    { title: t.tutorial.step1Title, desc: t.tutorial.step1Desc },
    { title: t.tutorial.step2Title, desc: t.tutorial.step2Desc },
    { title: t.tutorial.step3Title, desc: t.tutorial.step3Desc },
    { title: t.tutorial.step4Title, desc: t.tutorial.step4Desc },
    { title: t.tutorial.step5Title, desc: t.tutorial.step5Desc },
  ];

  return (
    <div className="tutorial-page">
      <SEO path="/tutorial" />
      <AppHeader
        onBack={() => nav('home')}
        backLabel={t.common.backToHome}
        title={t.tutorial.pageTitle}
      />

      <div className="tut-body">
        {/* Step progress dots */}
        <div className="tut-step-dots">
          {SCENARIOS.map((_, i) => (
            <span
              key={i}
              className={`tut-dot${i === step ? ' tut-dot--active' : i < step ? ' tut-dot--done' : ''}`}
            />
          ))}
        </div>

        {/* Board */}
        <div className="tut-board-wrap">
          <table className="tut-grid">
            <tbody>
              {Array.from({ length: 9 }, (_, r) => (
                <tr key={r}>
                  {Array.from({ length: 9 }, (_, c) => {
                    const val = getDisplayValue(r, c);
                    return (
                      <td key={c} className={getCellClass(r, c)}>
                        {val !== null && (
                          <span className="tut-cell-val">{val}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Info card */}
        <div className="tut-info">
          <h2 className="tut-step-title">{stepData[step].title}</h2>
          <p className="tut-step-desc">{stepData[step].desc}</p>
          {phase === 'success' && (
            <div className="tut-feedback tut-feedback--correct">{t.tutorial.correct}</div>
          )}
          {phase === 'wrong' && (
            <div className="tut-feedback tut-feedback--wrong">{t.tutorial.wrong}</div>
          )}
        </div>

        {/* Numpad — visible only on action steps when not yet succeeded */}
        {scenario.type === 'action' && phase !== 'success' && (
          <>
            <div className="tut-action-hint">
              {t.tutorial.actionHint}
            </div>
            <div className="tut-numpad">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => (
                <button
                  key={n}
                  className="tut-num"
                  onClick={() => handleInput(n)}
                  disabled={phase === 'wrong'}
                >
                  {n}
                </button>
              ))}
            </div>
          </>
        )}

        {/* Navigation */}
        <div className="tut-nav">
          {step > 0 && (
            <button className="tut-nav-btn tut-nav-btn--prev" onClick={goPrev}>
              {t.tutorial.prev}
            </button>
          )}
          {scenario.type === 'info' && (
            isLastStep ? (
              <button className="tut-nav-btn tut-nav-btn--finish" onClick={goNext}>
                {t.tutorial.finish}
              </button>
            ) : (
              <button className="tut-nav-btn tut-nav-btn--next" onClick={goNext}>
                {t.tutorial.next}
              </button>
            )
          )}
        </div>
      </div>
      <TutorialContent />
    </div>
  );
}
